import { Heart, Users, Award, Clock, MapPin, Phone, Mail, Shield } from 'lucide-react';

const About = () => {
    const stats = [
        { number: '15+', label: 'Years of Excellence', icon: Award },
        { number: '10,000+', label: 'Patients Served', icon: Users },
        { number: '24/7', label: 'Emergency Care', icon: Clock },
        { number: '98%', label: 'Patient Satisfaction', icon: Heart }
    ];

    const services = [
        {
            title: 'Primary Care',
            description: 'Comprehensive health screenings, preventive care, and routine check-ups for all ages.',
            icon: Heart
        },
        {
            title: 'Specialist Consultations',
            description: 'Access to top specialists in cardiology, dermatology, orthopedics, and more.',
            icon: Users
        },
        {
            title: 'Diagnostic Services',
            description: 'State-of-the-art imaging, laboratory tests, and diagnostic procedures.',
            icon: Shield
        },
        {
            title: 'Telemedicine',
            description: 'Virtual consultations and remote monitoring for your convenience.',
            icon: Clock
        }
    ];

    const team = [
        {
            name: 'Dr. Sarah Johnson',
            specialty: 'Chief Medical Officer',
            experience: '20 years of experience in Internal Medicine',
            image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=300&h=300&fit=crop&crop=face'
        },
        {
            name: 'Dr. Michael Chen',
            specialty: 'Cardiologist',
            experience: '15 years specializing in cardiovascular health',
            image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=300&h=300&fit=crop&crop=face'
        },
        {
            name: 'Dr. Emily Rodriguez',
            specialty: 'Pediatrician',
            experience: '12 years caring for children and adolescents',
            image: 'https://images.unsplash.com/photo-1594824804732-ca876019e1ff?w=300&h=300&fit=crop&crop=face'
        }
    ];

    return (
        <div className='min-h-screen bg-gradient-to-br from-purple-50 to-blue-50'>
            {/* Hero Section */}
            <div className='relative bg-gradient-to-r from-purple-900 via-purple-800 to-blue-900 text-white py-20'>
                <div className='absolute inset-0 bg-black opacity-20'></div>
                <div className='relative max-w-7xl mx-auto px-4 text-center'>
                    <h1 className='text-5xl md:text-6xl font-bold mb-6'>
                        About Our Practice
                    </h1>
                    <p className='text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed'>
                        Dedicated to providing exceptional healthcare with compassion, innovation, and excellence
                    </p>
                    <div className='inline-flex items-center space-x-2 bg-white bg-opacity-20 px-6 py-3 rounded-full backdrop-blur-sm'>
                        <Heart className='w-6 h-6' />
                        <span className='text-lg font-semibold'>Your Health, Our Priority</span>
                    </div>
                </div>
            </div>

            {/* Stats Section */}
            <div className='py-16 bg-white'>
                <div className='max-w-7xl mx-auto px-4'>
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'>
                        {stats.map((stat, index) => {
                            const Icon = stat.icon;
                            return (
                                <div key={index} className='text-center group hover:transform hover:scale-105 transition-all duration-300'>
                                    <div className='inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full mb-4 group-hover:shadow-lg'>
                                        <Icon className='w-8 h-8 text-white' />
                                    </div>
                                    <div className='text-4xl font-bold text-purple-900 mb-2'>{stat.number}</div>
                                    <div className='text-gray-600 font-medium'>{stat.label}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Mission Section */}
            <div className='py-20 bg-gradient-to-r from-purple-100 to-blue-100'>
                <div className='max-w-7xl mx-auto px-4'>
                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 items-center'>
                        <div>
                            <h2 className='text-4xl font-bold text-purple-900 mb-6'>Our Mission</h2>
                            <p className='text-lg text-gray-700 mb-6 leading-relaxed'>
                                We are committed to delivering personalized, comprehensive healthcare that puts patients first.
                                Our team of dedicated professionals combines cutting-edge medical technology with compassionate
                                care to ensure the best possible outcomes for every patient.
                            </p>
                            <p className='text-lg text-gray-700 mb-8 leading-relaxed'>
                                From preventive care to complex treatments, we believe in empowering our patients with
                                knowledge and involving them in every aspect of their healthcare journey.
                            </p>
                            <div className='flex items-center space-x-4'>
                                <div className='w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center'>
                                    <Shield className='w-6 h-6 text-white' />
                                </div>
                                <div>
                                    <div className='font-semibold text-purple-900'>HIPAA Compliant</div>
                                    <div className='text-gray-600'>Your privacy is our priority</div>
                                </div>
                            </div>
                        </div>
                        <div className='relative'>
                            <div className='bg-white rounded-2xl shadow-2xl p-8 transform rotate-3 hover:rotate-0 transition-transform duration-500'>
                                <img
                                    src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=600&h=400&fit=crop"
                                    alt="Modern medical facility"
                                    className='w-full h-64 object-cover rounded-xl mb-6'
                                />
                                <h3 className='text-xl font-bold text-purple-900 mb-2'>State-of-the-Art Facility</h3>
                                <p className='text-gray-600'>
                                    Our modern clinic features the latest medical equipment and comfortable
                                    amenities to ensure the best patient experience.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Services Section */}
            <div className='py-20 bg-white'>
                <div className='max-w-7xl mx-auto px-4'>
                    <div className='text-center mb-16'>
                        <h2 className='text-4xl font-bold text-purple-900 mb-4'>Our Services</h2>
                        <p className='text-xl text-gray-600 max-w-3xl mx-auto'>
                            Comprehensive healthcare services designed to meet all your medical needs under one roof
                        </p>
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'>
                        {services.map((service, index) => {
                            const Icon = service.icon;
                            return (
                                <div key={index} className='bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:translateY-2'>
                                    <div className='inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg mb-4'>
                                        <Icon className='w-6 h-6 text-white' />
                                    </div>
                                    <h3 className='text-xl font-bold text-purple-900 mb-3'>{service.title}</h3>
                                    <p className='text-gray-600 leading-relaxed'>{service.description}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Team Section */}
            <div className='py-20 bg-gradient-to-br from-purple-50 to-blue-50'>
                <div className='max-w-7xl mx-auto px-4'>
                    <div className='text-center mb-16'>
                        <h2 className='text-4xl font-bold text-purple-900 mb-4'>Meet Our Team</h2>
                        <p className='text-xl text-gray-600 max-w-3xl mx-auto'>
                            Our experienced physicians and healthcare professionals are dedicated to your wellbeing
                        </p>
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
                        {team.map((doctor, index) => (
                            <div key={index} className='bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group'>
                                <div className='relative overflow-hidden'>
                                    <img
                                        src={doctor.image}
                                        alt={doctor.name}
                                        className='w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300'
                                    />
                                    <div className='absolute inset-0 bg-gradient-to-t from-purple-900 via-transparent to-transparent opacity-0 group-hover:opacity-70 transition-opacity duration-300'></div>
                                </div>
                                <div className='p-6'>
                                    <h3 className='text-xl font-bold text-purple-900 mb-2'>{doctor.name}</h3>
                                    <div className='text-blue-600 font-semibold mb-3'>{doctor.specialty}</div>
                                    <p className='text-gray-600'>{doctor.experience}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Contact Section */}
            <div className='py-20 bg-gradient-to-r from-purple-900 via-purple-800 to-blue-900 text-white'>
                <div className='max-w-7xl mx-auto px-4'>
                    <div className='text-center mb-12'>
                        <h2 className='text-4xl font-bold mb-4'>Get In Touch</h2>
                        <p className='text-xl opacity-90 max-w-3xl mx-auto'>
                            Ready to take the next step in your healthcare journey? Contact us today to schedule an appointment
                        </p>
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
                        <div className='text-center group hover:transform hover:scale-105 transition-all duration-300'>
                            <div className='inline-flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-full mb-4 group-hover:bg-opacity-30'>
                                <MapPin className='w-8 h-8' />
                            </div>
                            <h3 className='text-xl font-bold mb-2'>Visit Us</h3>
                            <p className='opacity-90'>123 Medical Center Drive<br />Healthcare City, HC 12345</p>
                        </div>
                        <div className='text-center group hover:transform hover:scale-105 transition-all duration-300'>
                            <div className='inline-flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-full mb-4 group-hover:bg-opacity-30'>
                                <Phone className='w-8 h-8' />
                            </div>
                            <h3 className='text-xl font-bold mb-2'>Call Us</h3>
                            <p className='opacity-90'>(555) 123-CARE<br />24/7 Emergency Line</p>
                        </div>
                        <div className='text-center group hover:transform hover:scale-105 transition-all duration-300'>
                            <div className='inline-flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-full mb-4 group-hover:bg-opacity-30'>
                                <Mail className='w-8 h-8' />
                            </div>
                            <h3 className='text-xl font-bold mb-2'>Email Us</h3>
                            <p className='opacity-90'>info@healthcareportal.com<br />appointments@healthcareportal.com</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default About;